import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import {
    EventEntity,
    EventStateEnum,
    EventStateEnumNameMap, issueEventStates,
    ITaskPriority,
    taskEventStates,
} from '../../../dave-data-module/entities/event.entity';
import { State } from '../../../dave-data-module/State';
import { EventsActionTypes } from '../../../dave-data-module/State/actions/events.actions';
import { getCommissionDictionary } from '../../../dave-data-module/State/selectors/commission.selector';
import { getEventById, getTasks, getTasksFetched } from '../../../dave-data-module/State/selectors/events.selectors';
import { getUser } from '../../../dave-data-module/State/selectors/users.selectors';
import {
    DetailTasksComponent,
    DetailTasksComponentDialogData,
    DetailTasksComponentDialogDataInitialValues
} from "../../../dave-event-card/components/detail-tasks/detail-tasks.component";
import { BreakpointObserverService } from '../../../dave-utils-module/dave-shared-components-module/services/breakpoint-observer.service';
import { isNotNullOrUndefined, sortTasksByPriority } from '../../../helper/helper';
import {
    getUserToCommissionCommissionIdsMap, getUserToCommissionDictionary, getUserToCommissionFetched,
} from '../../../dave-data-module/State/selectors/user-to-commission.selector';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { UserToCommissionResolver } from '../../../dave-data-module/guards/user-to-commission.resolver';
import { CompactEnumType } from '../../../dave-event-card/components/event-card/event-card.component';


const PAGE_SIZE = 20;

export interface TaskBoardFilter {
    CommissionIds?: number[];
    UserId?: number[];
    AutorId?: number[];
    State?: EventStateEnum[];
    MaxEventEndDate?: Date;
    MinEventEndDate?: Date;
    Priority?: ITaskPriority[];
    MilestoneIds?: number[];
    ShowCompleted?: boolean;
}

interface lane {
    Headline: string;
    Id: EventStateEnum;
    EventIds: number[];
}
export interface ITaskBoardSort {
    sortBy: 'endDate' | 'priority';
    direction: 'asc' | 'dsc';
}
@Component({
    selector: 'app-tasks-board[FileClicked]',
    templateUrl: './tasks-board.component.html',
    styleUrls: ['./tasks-board.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasksBoardComponent implements OnInit {
    @Input() ShowNewButton = true;
    @Input() NewTaskInitialValues: DetailTasksComponentDialogDataInitialValues;
    @Input()
    set Search(search: string) {
        this.search$.next(search);
    }
    @Input()
    set Filter(filter: TaskBoardFilter) {
        this.filter$.next(filter);
    }
    @Input()
    set Sort(sort: ITaskBoardSort) {
        this.sort$.next(sort);
    }
    @Input() PreFilter: {
        authorOrUserToEventOrUserToCommission?: boolean;
        eventIds?: number[];
    };

    // MDQX: Ausgewählte Spalten für das Board
    @Input() SelectedBoardColumns: EventStateEnum[] = Object.values(taskEventStates) ?? [];
    @Input() showCompleted = false;
    @Input() showReleased = false;
    @Input() showOnHold = false;
    @Input() openedFromTaskPage = false;
    @Input() openedFromWeekPlan = false;
    @Output() FileClicked = new EventEmitter<number>();
    @Input() ExternalConnectedDropListIds: string[] = [];
    @Input() DragPreviewBackground: string | null = null;
    protected readonly CompactEnum = CompactEnumType;


    public isCompletedCollapsed = true;

    public collapsedLaneIds = new Set<EventStateEnum>();
    toggleLaneCollapsed(laneId: EventStateEnum): void {
        if (!this.openedFromWeekPlan) {
            return;
        }
        if (this.collapsedLaneIds.has(laneId)) {
            this.collapsedLaneIds.delete(laneId);
        } else {
            this.collapsedLaneIds.add(laneId);
        }
    }

    isLaneCollapsed(laneId: EventStateEnum): boolean {
        return this.openedFromWeekPlan && this.collapsedLaneIds.has(laneId);
    }

    private search$ = new BehaviorSubject<string>('');

    private filter$ = new BehaviorSubject<TaskBoardFilter>({});
    protected subscriptions: Subscription[] = [];
    public DisableDragNDrop$: Observable<boolean> = combineLatest([this.bd.TouchQuery, this.bd.MobileQuery]).pipe(map(([isTouch, mobile]) => isTouch && mobile));

    public sort$: BehaviorSubject<ITaskBoardSort> = new BehaviorSubject({ sortBy: 'endDate', direction: 'dsc' });
    private preFilteredAndSortedTasks$: Observable<EventEntity[]> = this.store$.select(getTasksFetched).pipe(
        filter((v) => !!v),
        switchMap(() => this.onInit$),
        filter((v) => !!v),
        switchMap(() => this.store$.select(getTasks).pipe(
            map((tasks) => tasks.filter((t) => !this.PreFilter || !this.PreFilter.eventIds || this.PreFilter.eventIds.includes(t.Id))),
            map((tasks) => (this.openedFromWeekPlan ? tasks.filter((t) => !t.ProcessingStartDate || t.UserIds?.length === 0) : tasks)),
        )),
        switchMap((tasks) =>
            this.PreFilter && this.PreFilter.authorOrUserToEventOrUserToCommission
                ? combineLatest([getFetched$(this.store$, getUserToCommissionFetched, getUserToCommissionCommissionIdsMap), getFetched$(this.store$, getUserToCommissionFetched, getUserToCommissionDictionary), this.store$.select(getUser)]).pipe(
                    map(([userToCommissions, userToCommissionDict, user]) =>
                        tasks.filter((t) => t.UserId === user.Id || t.UserIds?.includes(user.Id) || (t.CommissionId && userToCommissions.get(t.CommissionId)?.some(id => userToCommissionDict[id].UserId === user.Id))),
                    ),
                )
                : of(tasks),
        ),
        map((tasks) =>
            tasks.sort((a, b) => {
                const aDate = a.EventEndDate;
                const bDate = b.EventEndDate;
                if (aDate == null && bDate == null) {
                    return 0;
                }
                if (aDate == null && bDate != null) {
                    return 1;
                }
                if (aDate != null && bDate == null) {
                    return -1;
                }
                return aDate.getTime() - bDate.getTime();
            }),
        ),
        switchMap((tasks) =>
            this.filter$.pipe(
                map((filter) => {
                    let filteredTasks = tasks.slice();
                    if (filter?.AutorId?.length) {
                        filteredTasks = filteredTasks.filter((e) => filter.AutorId.includes(e.UserId));
                    }
                    if (filter?.CommissionIds?.length) {
                        filteredTasks = filteredTasks.filter((e) => filter.CommissionIds.includes(e.CommissionId));
                    }
                    if (filter?.MilestoneIds?.length) {
                        filteredTasks = filteredTasks.filter((e) => filter.MilestoneIds.some(mId => e.MilestoneIds.includes(mId)));
                    }
                    if (filter?.UserId?.length) {
                        filteredTasks = filteredTasks.filter((e) => filter.UserId.some(fid => e.UserIds?.includes(fid)));
                    }
                    if (filter?.State) {
                        filteredTasks = filteredTasks.filter((e) => filter.State.includes(e.State));
                    }
                    if (filter?.MaxEventEndDate) {
                        filteredTasks = filteredTasks.filter((e) => e.EventEndDate && filter.MaxEventEndDate.getTime() > e.EventEndDate.getTime());
                    }
                    if (filter?.MinEventEndDate) {
                        filteredTasks = filteredTasks.filter((e) => e.EventEndDate && filter.MinEventEndDate.getTime() < e.EventEndDate.getTime());
                    }
                    if (filter?.Priority?.length) {
                        filteredTasks = filteredTasks.filter((e) => isNotNullOrUndefined(e.AdditionalData?.Priority) && filter.Priority.includes(e.AdditionalData.Priority));
                    }
                    return filteredTasks;
                }),
            ),
        ),
        switchMap((tasks) =>
            this.sort$.pipe(
                distinctUntilChanged((a, b) => a.sortBy === b.sortBy && a.direction === b.direction),
                map((sort) => {
                    switch (sort.sortBy) {
                        case 'endDate':
                            return sort.direction === 'dsc' ? tasks : tasks.slice().reverse();
                        case 'priority':
                            return sortTasksByPriority(tasks, sort.direction);  // MDQX: Sortierung nach Priorität (wichtig, normal, weniger wichtig)
                    }
                }),
            ),
        ),
    ).pipe(
        switchMap((tasks) =>
            this.search$.pipe(
                map((value) => {
                    const searchTerm = (value || '').toLowerCase();
                    return searchTerm;
                }),
                map((searchTerm) => {
                    const filteredTasks = searchTerm
                        ? tasks.filter((data) => {
                            const values = ['', data?.Hint || '', data?.Description || '', data?.Name || '']
                                .map((value) => value?.trim().toLowerCase());
                            return values.some((value) => value?.includes(searchTerm));
                        })
                        : tasks;

                    console.log('Filtered tasks:', filteredTasks);
                    return filteredTasks;
                }),
            ),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    public NewSwimLanes: {
        Headline: string;
        Id: EventStateEnum;
        eventIds$: Observable<number[]>;
        page$: BehaviorSubject<number>;
        isLastPage$: Observable<boolean>;
    }[] = Object.values(taskEventStates).map((state) => {
        const page$ = new BehaviorSubject(1);
        const eventIds$ = this.preFilteredAndSortedTasks$.pipe(
            switchMap((tasks) =>
                this.search$.pipe(
                    map((value) => {
                        const searchTerm = (value || '').toLowerCase();
                        return searchTerm;
                    }),

                    map((searchTerm) => {
                        const filteredTasks = searchTerm
                            ? tasks.filter((data) => {
                                const values = ['', data?.Hint || '', data?.Description || '', data?.Name || '']
                                    .map((value) => value?.trim().toLowerCase());
                                return values.some((value) => value?.includes(searchTerm));
                            })
                            : tasks;
                        return filteredTasks;
                    }),
                    map((filteredTasks) =>
                        filteredTasks.filter((task) => task.State === state).map((task) => task.Id)
                    )
                )
            ),
            shareReplay({ bufferSize: 1, refCount: true }),
        );


        return {
        Headline: EventStateEnumNameMap.get(state),
        Id: state,
        page$,
        isLastPage$: combineLatest([page$, eventIds$]).pipe(map(([page, eventIds]) => {
            console.log({ page, eventIds });
            return page >= (eventIds.length / PAGE_SIZE)
        })),
        eventIds$: eventIds$.pipe(
            switchMap(events => {
            const maxPageSize = Math.ceil(events.length / PAGE_SIZE);

            page$.next(1);
            return page$.pipe(
                map(p => p > maxPageSize ? maxPageSize : p),
                map(p => events.slice(0, p * PAGE_SIZE)),
                distinctUntilChanged(),
            )
        }),),
    }});


    private onInit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    constructor(private store$: Store<State>, private dialog: MatDialog, private bd: BreakpointObserverService, u2cResolver: UserToCommissionResolver) {
        firstValueFrom(this.store$.select(getUserToCommissionFetched)).then(f => {
            if (!f) {
                u2cResolver.resolve();
            }
        })
    }
    ngOnInit(): void {
        this.onInit$.next(true);
   }

    nextPage(page$: BehaviorSubject<number>, pages = 1) {
        page$.next(page$.value+pages);
    }

    nextPageAllLanes(pages = 1) {
        this.NewSwimLanes.forEach(({page$}) => this.nextPage(page$, pages));
    }

    toggleCompleted() {
        this.isCompletedCollapsed = !this.isCompletedCollapsed;
    }


    Drop(event: CdkDragDrop<EventStateEnum, EventStateEnum, number>) {
        if (event.previousContainer !== event.container) {
            const id = event.item.data;
            const state = event.container.data;
            firstValueFrom(this.store$.select(getEventById({ id }))).then((e) => {
                this.store$.dispatch(EventsActionTypes.UpdateEvent({ Payload: e.Clone({ State: event.container.data }) }));
            });
            this.store$.dispatch(EventsActionTypes.ModifyEvent({ Payload: { id, state } }));
        }
    }
    OpenAddTaskDialog() {
        this.dialog.open<DetailTasksComponent, DetailTasksComponentDialogData>(DetailTasksComponent, {
            ...DetailTasksComponent.DefaultConfig,
            data: {
                EventId: null,
                InitialValues: {
                    CommissionId: this.filter$.value?.CommissionIds?.length === 1 ? this.filter$.value?.CommissionIds[0] : null,
                    ...(this.NewTaskInitialValues || {})
                },
            },
        });
    }

    TrackLanes(index: number, lane: { Id: EventStateEnum }) {
        return lane.Id;
    }

    protected readonly EventStateEnumNameMap = EventStateEnumNameMap;
    protected readonly EventStateEnum = EventStateEnum;

    protected filterLanes(lane: {Headline: string; Id: EventStateEnum; eventIds$: Observable<number[]>; page$: BehaviorSubject<number>; isLastPage$: Observable<boolean>} & {Id: EventStateEnum}) {
        if (this.openedFromTaskPage || this.openedFromWeekPlan) {
            if (lane.Headline === 'Erledigt' && !this.showCompleted) {
                return false;

            }
            if (lane.Headline === 'Rückmeldung' && !this.showReleased) {
                return false;
            }
            if (lane.Headline === 'On hold' && !this.showOnHold) {
                return false;
            }
            return true;

        }else{
           return  this.SelectedBoardColumns.includes(lane.Id)
        }
    }
}
